<template>
  <v-card dark class="d-flex flex-column" style="height: 100vh">
    <v-card-title class="black white--text">
      <span class="yellow--text mr-2">HYROX</span> Admin
      <v-spacer></v-spacer>
      <v-btn icon dark @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>

    <v-card-text class="pa-4">
      <v-data-table v-model="selected" :headers="headers" :items="reservations" :search="search" item-key="_id" class="elevation-1">
        <template v-slot:top>
          <div class="d-flex align-center pa-4">
            <v-btn color="primary" @click="openMailDialog" class="mr-4">
              <v-icon left>mdi-email-fast-outline</v-icon>
              Coupon Mail Senden
            </v-btn>

            <v-text-field v-model="search" label="Suche" hide-details append-icon="mdi-magnify"></v-text-field>
          </div>
        </template>

        <template v-slot:item.createdBy="{ item }">
          <div v-if="item.createdBy">
            <strong>{{ item.createdBy.vorname }} {{ item.createdBy.nachname }}</strong
            ><br />
            <small>{{ item.createdBy.email }}</small>
          </div>
          <div v-else>Unbekannt</div>
        </template>

        <template v-slot:item.content.ticketDivision="{ item }">
          <v-chip x-small color="grey darken-3" dark>{{ item.content.ticketDivision }}</v-chip>
        </template>

        <template v-slot:item.content.ticketPrice="{ item }"> CHF {{ item.content.ticketPrice }} </template>

        <template v-slot:item.content.status="{ item }">
          <v-chip :color="getStatusColor(item.content.status)" dark small>
            {{ formatStatus(item.content.status) }}
          </v-chip>
        </template>

        <template v-slot:item.created="{ item }">
          {{ formatDate(item.created) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="updateStatus(item, 'sent')" v-if="item.content.status === 'reserved'" v-bind="attrs" v-on="on">
                <v-icon color="blue">mdi-check-all</v-icon>
              </v-btn>
            </template>
            <span>Nur Status auf 'Versendet' setzen (Keine Mail)</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                @click="updateStatus(item, 'canceled')"
                color="red"
                v-if="item.content.status !== 'canceled'"
                v-bind="attrs"
                v-on="on">
                <v-icon>mdi-cancel</v-icon>
              </v-btn>
            </template>
            <span>Stornieren / Freigeben</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>

    <v-dialog v-model="mailDialog" max-width="600px">
      <v-card dark>
        <v-card-title class="headline">Coupon versenden</v-card-title>
        <v-card-subtitle class="mt-1">
          <span v-if="selected.length > 0">An: {{ selected[0].createdBy ? selected[0].createdBy.vorname : "Unbekannt" }}</span>
          <span v-else>Manueller Versand</span>
        </v-card-subtitle>

        <v-card-text>
          <v-form ref="mailForm" v-model="mailFormValid">
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field v-model="mailData.firstName" label="Vorname (für Anrede)" required></v-text-field>
              </v-col>

              <v-col cols="12" sm="6">
                <v-text-field v-model="mailData.division" label="Kategorie (Division)" required></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="mailData.email"
                  label="E-Mail Adresse Empfänger"
                  outlined
                  required
                  type="email"
                  :rules="emailRules"></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-switch v-model="mailData.ma" label="GRATIS TICKET"> </v-switch>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="mailData.couponCode"
                  label="Coupon Code"
                  outlined
                  required
                  background-color="yellow lighten-4"
                  light
                  :rules="[(v) => !!v || 'Code ist erforderlich']"></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="mailDialog = false">Abbrechen</v-btn>
          <v-btn color="blue" dark @click="sendMail" :disabled="!mailFormValid || mailLoading" :loading="mailLoading">
            <v-icon left>mdi-send</v-icon>
            Jetzt Senden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  data: () => ({
    search: "",
    reservations: [],
    selected: [], // Für die Checkboxen (show-select)

    // Dialog Vars
    mailDialog: false,
    mailFormValid: false,
    mailLoading: false,

    mailData: {
      reservationId: null, // Optional, falls verknüpft
      firstName: "",
      email: "",
      division: "",
      couponCode: "",
      ma: false,
    },

    emailRules: [(v) => !!v || "E-Mail ist erforderlich", (v) => /.+@.+\..+/.test(v) || "E-Mail muss gültig sein"],

    headers: [
      { text: "Kunde", value: "createdBy" },
      { text: "Ticket", value: "content.ticketDivision" },
      { text: "Preis", value: "content.ticketPrice" },
      { text: "Status", value: "content.status" },
      { text: "Datum", value: "created" },
      { text: "Actions", value: "actions", sortable: false },
    ],
  }),
  async created() {
    this.reservations = await api.getHyroxAdminReservations()
  },
  methods: {
    formatDate(val) {
      if (!val) return "-"
      const date = new Date(val)
      if (isNaN(date.getTime())) return val
      return date.toLocaleString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    formatStatus(status) {
      if (status === "sent") return "E-MAIL VERSENDET"
      if (status === "canceled") return "STORNIERT"
      if (status === "reserved") return "RESERVIERT"
      return status ? status.toUpperCase() : "UNBEKANNT"
    },
    getStatusColor(status) {
      if (status === "reserved") return "orange"
      if (status === "sent") return "green"
      if (status === "canceled") return "red"
      return "grey"
    },

    // Öffnet den Dialog - füllt Daten, falls eine Zeile ausgewählt ist
    openMailDialog() {
      // Reset
      this.mailData = {
        reservationId: null,
        firstName: "",
        email: "",
        division: "",
        couponCode: "",
        ma: false,
      }

      // Wenn genau 1 Zeile ausgewählt ist, vorbefüllen
      if (this.selected.length === 1) {
        const item = this.selected[0]
        this.mailData.reservationId = item._id
        this.mailData.firstName = item.createdBy ? item.createdBy.vorname : ""
        this.mailData.email = item.createdBy ? item.createdBy.email : ""
        this.mailData.division = item.content.ticketDivision || ""
        this.mailData.division = item.content.ticketDivision || ""
        // Coupon Code bleibt leer, den musst du reinkopieren
      }

      this.mailDialog = true
    },

    async sendMail() {
      if (!this.$refs.mailForm.validate()) return

      this.mailLoading = true
      try {
        await api.sendHyroxTicketMail(this.mailData)

        // Wenn wir eine ID hatten (also über Tabelle ausgewählt), updaten wir lokal den Status
        if (this.mailData.reservationId) {
          const item = this.reservations.find((r) => r._id === this.mailData.reservationId)
          if (item) {
            item.content.status = "sent"
            item.content.couponCode = this.mailData.couponCode
          }
          // Auswahl aufheben
          this.selected = []
        }

        this.$emit("show-snackbar", "Mail erfolgreich versendet!")
        this.mailDialog = false
      } catch (error) {
        console.error(error)
        alert("Fehler beim Senden: " + (error.response?.data || error.message))
      } finally {
        this.mailLoading = false
      }
    },

    async updateStatus(item, status) {
      let msg = `Status auf ${status} setzen?`
      if (status === "sent") msg = "Nur Status ändern? (Es wird KEINE E-Mail versendet)"
      if (status === "canceled") msg = "Reservierung wirklich STORNIEREN? Ticket wird freigegeben."

      if (!confirm(msg)) return

      try {
        await api.updateHyroxStatus(item._id, status)
        item.content.status = status
      } catch (error) {
        alert("Fehler: " + error.message)
      }
    },
  },
}
</script>
